import { useMemo, useEffect, useState } from 'react';
import {
  Row,
  ContainerProps,
  styled,
  Title,
  Pagination,
  Color,
  SubTitle,
  Container,
  Col,
  Shape,
  Flex,
  Body,
} from '@teko/ds-desktop';
import { VerticalFilter, SelectionOption } from '@teko/teko-ecommerce';
import { useTenantConfig, usePos365StoreCode, useSearch } from 'hooks';
import { t } from 'i18n';
import { useRouter } from 'next/router';
import Head from 'next/head';
import {
  omitQuery,
  getActiveSortOption,
  extractSortQuery,
  DEFAUL_SORT_OPTIONS,
  getDisplayFilter,
  cookFilterPrice,
  getCollectionSlug,
  getSignatureQueryParam,
} from '../shared/helpers';
import { DESKTOP_ITEM_PER_PAGE_IN_LISTING_PAGE } from '../../../constants';
import { SizeEnum } from '@teko/types/ds';
import dynamic from 'next/dynamic';
import { useFilter } from '../hooks/useFilter';
import {
  SearchV2Params,
  RETURN_FILTERABLE,
  SearchProductFilter,
} from '@teko/discovery/v2';
import {
  stringToInteger,
  scheduleTaskInIdleTime,
  cleanObject,
} from '@teko/utils';
import { trackViewProductList } from '@teko/tracking';
import { ProductCardProps } from '@teko/types/ecommerce';
import { ParsedUrlQueryInput } from 'querystring';
import {
  convertDiscoveryFilter,
  convertToPayloadSorting,
  convertToPayloadFilter,
} from 'helpers';
import { SeoInfo } from '../shared/types';

const NoProductsFound = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.NoProductsFound),
);

const ProductList = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.ProductList),
);

const ProductGrid = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.ProductGrid),
);

const DiscoveryRecommendedProducts = dynamic(() =>
  import('components/DiscoveryRecommendedProducts').then(
    (mod) => mod.DiscoveryRecommendedProducts,
  ),
);

interface ProductListWithFilterProps extends Omit<ContainerProps, 'children'> {
  siteTitle?: string;
  serverFilters?: SearchProductFilter;
  serverProducts?: ProductCardProps[];
  serverPagination?: {
    totalItems: number;
    totalPages: number;
  };
  filterPrice?: {
    max: number;
    min: number;
  };
  seoInfo?: SeoInfo;
}

export const ProductListWithFilter = ({
  siteTitle,
  serverProducts,
  serverFilters = {},
  serverPagination,
  filterPrice,
  seoInfo,
  ...props
}: ProductListWithFilterProps) => {
  const router = useRouter();
  const { query, asPath } = router;
  const {
    showCollectionPageTitle,
    defaultSortOptionCode,
    addSchemaItemList,
    baseDomain,
    meta,
  } = useTenantConfig();
  let clientFilterPrice = filterPrice;
  const [newQuery, setNewQuery] = useState<ParsedUrlQueryInput>(query);
  const {
    clearFilterLink,
    getActiveFilterOptions,
    onClickFilterOption,
    onSortByPrice,
    onSortOptionSelected,
    onChangePage,
  } = useFilter(newQuery, setNewQuery, { autoSubmit: true });

  let filters = serverFilters;

  const { pos365StoreCode } = usePos365StoreCode();
  const slug = getCollectionSlug(query.slug as string);
  const { providerId, sellerId, brandId, brands, page } = query;
  // to avoid sending duplicate GTM events
  const [, setProductSkuList] = useState<string[]>([]);
  // sort
  const activeSortOption = getActiveSortOption(query, defaultSortOptionCode);
  const [sort, order] = activeSortOption
    ? extractSortQuery(activeSortOption.optionCode)
    : [undefined, undefined];
  const payloadFilters = {
    // brandId --> case brand page, example: /asus-brand.asus
    // brands --> case filter brand: example: /search?router=productListing&query=áo&brands=aimer%2Cbare
    brands: brandId
      ? typeof brandId === 'string'
        ? [brandId]
        : [...brandId]
      : brands,
    // seller page: /*-sel.:sellerId
    sellerIds: sellerId && [sellerId],
    // provider page: /cua-hang/335?pageTitle=Takashimaya%20Official%20Store
    providerIds: providerId && [providerId],
    // other query --> for search page filter
    ...query,
  };

  const { isLoading, result } = useSearch(
    cleanObject({
      page,
      pageSize: DESKTOP_ITEM_PER_PAGE_IN_LISTING_PAGE,
      slug,
      query: query.query,
      filter: convertToPayloadFilter(payloadFilters),
      sorting: convertToPayloadSorting(sort, order),
      returnFilterable: [],
    }) as SearchV2Params,
    {
      products: serverProducts,
      pagination: serverPagination,
      filters: {} as SearchProductFilter,
    },
  );

  const { pagination, products } = result || {};
  const { totalItems, totalPages } = pagination || {};

  // currently, only serverside render filter for collection and brand page
  if (!slug && !brandId) {
    // call discovery search to get products
    const signatureFilterQueryParams = getSignatureQueryParam(query);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { result: filterResult } = useSearch(
      cleanObject({
        slug,
        query: query.query,
        filter: !query.query
          ? convertToPayloadFilter(signatureFilterQueryParams)
          : {},
        returnFilterable: RETURN_FILTERABLE,
      }) as SearchV2Params,
      {
        products: [],
        pagination: serverPagination,
        filters: serverFilters,
      },
    );
    clientFilterPrice = {
      max: (filterResult?.filters?.priceLte as number) || 10000000,
      min: (filterResult?.filters?.priceGte as number) || 0,
    };
    filters = filterResult?.filters as SearchProductFilter;
  }

  // send GTM track view product list event
  useEffect(() => {
    scheduleTaskInIdleTime(() => {
      setProductSkuList((prevSkuList) => {
        const newSkuList = products?.map((product) => product.sku || '') ?? [];

        // check if these 2 arrays are different
        const different =
          prevSkuList.length !== newSkuList?.length ||
          prevSkuList.find((sku) => !newSkuList.includes(sku));

        if (different) {
          trackViewProductList(
            products?.map((product) => ({
              sku: product.sku || '',
            })) ?? [],
          );
          return newSkuList;
        }
        return prevSkuList;
      });
    }, 3000);
  }, [products]);

  // filter price
  const { price_gte = '', price_lte = '' } = query;
  const { priceGte, priceLte, priceFilter } = useMemo(() => {
    const priceFilter = cookFilterPrice(clientFilterPrice?.max);
    return {
      priceGte: price_gte
        ? stringToInteger(price_gte.toString())
        : priceFilter.min,
      priceLte: price_lte
        ? stringToInteger(price_lte.toString())
        : priceFilter.max,
      priceFilter,
    };
  }, [price_gte, price_lte, clientFilterPrice?.max]);

  // render page title for collection page
  const renderPageTitle = () =>
    !!showCollectionPageTitle && !!siteTitle ? (
      <PageTitleContainer justify="start" align="baseline" borderRadius="m">
        <PageTitle>{(page ? `Trang ${page} - ` : '') + siteTitle}</PageTitle>
        {totalItems != undefined && (
          <Title color="textSecondary" paddingLeft="s8" weight="regular">
            {t('({{quantity}} sản phẩm)', {
              quantity: totalItems,
            })}
          </Title>
        )}
      </PageTitleContainer>
    ) : null;

  // render sort bar
  const renderSortBar = () => (
    <SortBarWrapper>
      <Flex justify="center" align="center" height={'100%'} paddingLeft="s16">
        <SubTitle marginRight="s32">{t('Sắp xếp theo')}</SubTitle>
        {DEFAUL_SORT_OPTIONS.map((option, index: number) => (
          <SelectionOption
            key={index}
            iconActiveSize={SizeEnum.xs}
            block={false}
            isActive={activeSortOption?.optionCode === option.optionCode}
            onClick={() => onSortOptionSelected(option)}
            style={{ padding: '0.5rem', marginRight: '1rem' }}
          >
            <Body level={1}>{t(option.optionName)}</Body>
          </SelectionOption>
        ))}
      </Flex>
    </SortBarWrapper>
  );

  // render product list
  const renderProductList = () => (
    <>
      {!products?.length && !isLoading && (
        <>
          <StyledNoProductsFound show={!isLoading && !products?.length} />
          <StyledContainer>
            <DiscoveryRecommendedProducts />
          </StyledContainer>
        </>
      )}
      {!!pos365StoreCode ? (
        <ProductList
          products={products}
          regionName="itemProductResult"
          itemsPerPage={
            Math.ceil(totalItems / totalPages) ||
            DESKTOP_ITEM_PER_PAGE_IN_LISTING_PAGE
          }
        />
      ) : (
        <ProductGrid
          products={products}
          regionName="itemProductResult"
          itemsPerPage={
            Math.ceil(totalItems / totalPages) ||
            DESKTOP_ITEM_PER_PAGE_IN_LISTING_PAGE
          }
        />
      )}
    </>
  );

  // render pagination
  const renderPagination = () => {
    const pageHref = (targetPage: string | number) => {
      const { page, ...keyword } = query;
      const omittedQuery = omitQuery(keyword);
      const rootPath = asPath.split('?')[0];
      return targetPage === 1
        ? `${rootPath}${
            Object.keys(omittedQuery).length === 0
              ? ''
              : `?${new URLSearchParams({ ...omittedQuery })}`
          }`
        : `${rootPath}?${new URLSearchParams({
            ...omittedQuery,
            page: targetPage.toString(),
          })}`;
    };
    return totalItems && totalPages && totalPages > 1 ? (
      <StyledPagination
        totalRecords={totalItems}
        pageLimit={DESKTOP_ITEM_PER_PAGE_IN_LISTING_PAGE}
        pageNeighbor={2}
        currentPage={parseInt(query.page ? query.page.toString() : '1')}
        onPageChange={onChangePage}
        itemRender={{
          page: (page, state) => (
            <PaginationItem
              {...state}
              onClick={(e) => {
                e.preventDefault();
                !state.disabled && onChangePage(Number(page));
              }}
              href={pageHref(page)}
            >
              <SubTitle>{page}</SubTitle>
            </PaginationItem>
          ),
        }}
      />
    ) : null;
  };

  const queryOmitSlug = useMemo(() => ({ ...query }), [query]);
  delete queryOmitSlug.slug;
  const productsSchema = useMemo(
    () =>
      !Object.keys(queryOmitSlug).length ||
      (Object.keys(queryOmitSlug).length === 1 &&
        Number(queryOmitSlug.page) === 1)
        ? products
        : [],
    [queryOmitSlug, products],
  );

  return (
    <>
      {addSchemaItemList && !!productsSchema?.length ? (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'ItemList',
                name: seoInfo?.siteTitle,
                url: `${baseDomain}${asPath}`,
                description: seoInfo?.description || meta.description,
                itemListElement: productsSchema.map((product, index) => ({
                  '@type': 'ListItem',
                  position: index + 1,
                  item: {
                    name: product.name,
                    url:
                      baseDomain +
                      (product.link?.as as { pathname: string })?.pathname,
                  },
                })),
              }),
            }}
          />
        </Head>
      ) : null}
      <Container {...props}>
        <Row gutter={[16, 16]} marginBottom="s16">
          <Col span={2}>
            <VerticalFilter
              activeOptions={getActiveFilterOptions(
                convertDiscoveryFilter(filters),
              )}
              filters={getDisplayFilter(convertDiscoveryFilter(filters))}
              onClickOption={onClickFilterOption}
              clearFilterLink={{
                href: clearFilterLink,
              }}
              filterPrice={
                priceFilter
                  ? {
                      max: priceFilter.max,
                      min: priceFilter.min,
                      step: priceFilter.step,
                      value: {
                        gte: priceGte,
                        lte: priceLte,
                      },
                      onFilter: onSortByPrice,
                    }
                  : undefined
              }
            />
          </Col>
          <Col span={10}>
            {renderPageTitle()}
            {
              <Shape backgroundColor="white" width="100%" borderRadius="m">
                {renderSortBar()}
                {renderProductList()}
              </Shape>
            }
            <Row marginTop="s16">{renderPagination()}</Row>
          </Col>
        </Row>
      </Container>
    </>
  );
};

const PageTitleContainer = styled(Row)`
  background-color: ${({ theme }) => theme.colors.white};
  margin-bottom: 0.25rem;
  padding: 0.75rem 1rem;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  line-height: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const SortBarWrapper = styled(Flex)`
  width: 100%;
  height: 64px;
  border-bottom: 1px solid #eaeaea;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  justify-content: space-between;
  font-size: 14px;
`;

const StyledNoProductsFound = styled(NoProductsFound)`
  margin-top: 4rem;
  margin-bottom: 4rem;
`;

const StyledPagination = styled(Pagination)`
  margin: auto;
`;

const StyledContainer = styled(Container)`
  max-width: 100%;
  margin-bottom: 24px;
`;

const PaginationItem = styled.a<{
  active?: boolean;
  disabled?: boolean;
  color?: string | Color;
}>(
  ({ theme, active, disabled, color }) => `
  border-radius: ${theme?.borderRadius.s};
  background-color: ${
    active ? theme.colors?.[color as Color] || color : theme?.colors.white
  };
  border: ${active ? 'none' : `1px solid ${theme?.colors.divider}`};
  pointer-events: ${disabled ? 'none' : 'auto'};
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content:center;
  color: ${active ? theme?.colors.white : 'currentColor'};
  text-decoration: none;
`,
);
